// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

// Pure, dependency-free DSP helpers. Everything here is unit-tested; the worker
// and UI import from this module so the maths lives in one place.

/** Average N channel arrays (already the same length) into one mono channel. */
export function downmixToMono(channels: Float32Array[]): Float32Array {
  if (channels.length === 0) return new Float32Array(0);
  if (channels.length === 1) return channels[0];
  const len = channels[0].length;
  const out = new Float32Array(len);
  const n = channels.length;
  for (let c = 0; c < n; c++) {
    const ch = channels[c];
    for (let i = 0; i < len; i++) out[i] += ch[i];
  }
  const inv = 1 / n;
  for (let i = 0; i < len; i++) out[i] *= inv;
  return out;
}

/**
 * Resample a mono signal from `inRate` to `outRate` with linear interpolation.
 * Good enough for a speech denoiser front-end (RNNoise is bandlimited anyway).
 */
export function resampleLinear(
  input: Float32Array,
  inRate: number,
  outRate: number,
): Float32Array {
  if (inRate === outRate || input.length === 0) return input;
  const ratio = outRate / inRate;
  const outLen = Math.max(1, Math.round(input.length * ratio));
  const out = new Float32Array(outLen);
  const step = inRate / outRate; // input samples advanced per output sample
  for (let i = 0; i < outLen; i++) {
    const srcPos = i * step;
    const i0 = Math.floor(srcPos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = srcPos - i0;
    out[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return out;
}

/** Root-mean-square of a signal (or a slice of it). */
export function rms(samples: Float32Array, start = 0, end = samples.length): number {
  if (end <= start) return 0;
  let sum = 0;
  for (let i = start; i < end; i++) {
    const s = samples[i];
    sum += s * s;
  }
  return Math.sqrt(sum / (end - start));
}

/** Convert a linear amplitude (0..1) to dBFS. Clamps the floor to -120 dB. */
export function toDb(amplitude: number): number {
  if (amplitude <= 1e-6) return -120;
  return 20 * Math.log10(amplitude);
}

/**
 * Estimate the broadband noise floor of a signal in dBFS by taking the RMS of
 * the quietest frames. `vadMask`, when supplied, marks speech frames (true) that
 * are excluded; otherwise we fall back to a low-percentile of frame energies.
 *
 * @param frameSize frames to chunk into (samples)
 * @param percentile lowest fraction of frame energies to average (0..1)
 */
export function estimateNoiseFloorDb(
  samples: Float32Array,
  frameSize: number,
  vadMask?: boolean[],
  percentile = 0.2,
): number {
  if (samples.length === 0) return -120;
  const energies: number[] = [];
  const nFrames = Math.floor(samples.length / frameSize);
  for (let f = 0; f < nFrames; f++) {
    if (vadMask && vadMask[f]) continue; // skip speech frames
    energies.push(rms(samples, f * frameSize, (f + 1) * frameSize));
  }
  if (energies.length === 0) {
    // Everything was speech (or one short frame) — use the global RMS.
    return toDb(rms(samples));
  }
  energies.sort((a, b) => a - b);
  const take = Math.max(1, Math.floor(energies.length * clamp(percentile, 0.01, 1)));
  let sum = 0;
  for (let i = 0; i < take; i++) sum += energies[i];
  return toDb(sum / take);
}

/** Clamp a number to [lo, hi]. */
export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/**
 * Blend a dry (original) and wet (denoised) signal. `amount` is 0..1 where 1 is
 * fully denoised and 0 is the original. Both arrays must be the same length.
 * Returns a new array (never mutates the inputs).
 */
export function mixDryWet(
  dry: Float32Array,
  wet: Float32Array,
  amount: number,
): Float32Array {
  const a = clamp(amount, 0, 1);
  const len = Math.min(dry.length, wet.length);
  const out = new Float32Array(len);
  const inv = 1 - a;
  for (let i = 0; i < len; i++) out[i] = dry[i] * inv + wet[i] * a;
  return out;
}

/** Peak absolute sample value of a signal. */
export function peak(samples: Float32Array): number {
  let m = 0;
  for (let i = 0; i < samples.length; i++) {
    const a = Math.abs(samples[i]);
    if (a > m) m = a;
  }
  return m;
}

/**
 * Scale a signal so its peak reaches `targetPeak` (default 0.98), guarding against
 * clipping. Returns a new array. A near-silent signal is returned unchanged.
 */
export function normalizePeak(samples: Float32Array, targetPeak = 0.98): Float32Array {
  const p = peak(samples);
  if (p < 1e-4) return samples.slice();
  const gain = targetPeak / p;
  if (gain >= 1) return samples.slice(); // never boost noise into clipping territory
  const out = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i++) out[i] = samples[i] * gain;
  return out;
}

/** Human-readable byte size. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[u]}`;
}

/** Human-readable clock duration from seconds, e.g. 83 -> "1:23". */
export function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * A safe output filename: strip the input extension, append a suffix + new ext.
 * e.g. buildOutputName("interview.m4a", "wav") -> "interview-cleaned.wav"
 */
export function buildOutputName(sourceName: string, ext: string): string {
  const base = sourceName.replace(/\.[^./\\]+$/, '').replace(/[\\/]+/g, '_').trim();
  const safe = base.length ? base : 'recording';
  return `${safe}-cleaned.${ext}`;
}
